// Pure logic of the Automatisation page (AIAutomation.js): rule coverage,
// the tiles from GET /api/ai-automation/logs/stats, « À vérifier », the
// « Qui peut envoyer seul » overview and the request shapes (unchanged).
//
// How the backend decides (Backend app/services/ai_automation_service.py
// get_automation_rules + reply_autonomy.verdict_for): for a reply of brand B
// and scope S (client | carrier), the rule is B's rule whose scope is S or
// « all » when B has one — active or not — else the « Toutes les marques »
// rule of that scope. An active rule makes the model judge the reply (sent
// alone when the confidence reaches the threshold, else a draft). Without an
// active rule, the B2B client's own setting decides when the client is
// configured (the account's legacy switch users.automation, off everywhere,
// is no longer shown: automation is enabled per client / carrier, Kevin
// 29/10). The rule fallback (fallback_action / fallback_employee_id) is never
// read by the backend: not shown, its stored values ride along untouched.

export const SCOPES = ['all', 'client', 'carrier'];
export const PERIODS = [7, 30, 90, 365];
export const LOGS_PAGE_SIZE = 20;

const scopesOf = (scope) => (scope === 'all' ? ['client', 'carrier'] : [scope]);
const sameBrand = (a, b) => String(a || '') === String(b || '');
export const brandIdOf = (brand) => String(brand?.id || brand?._id || '');

// One row per scope of the coverage list: « Toutes les marques » first,
// then each brand. `state`: own (its rules are listed) | covered (the
// global active rule applies) | none (no rule: client / account decides).
export const coverageRows = (rules, brands) => {
  const list = Array.isArray(rules) ? rules : [];
  const globalActive = list.some((rule) => !rule.brand_id && rule.is_active);
  const scopes = [{ brandId: '', brand: null }, ...(brands || []).map((brand) => ({ brandId: brandIdOf(brand), brand }))];
  return scopes.map(({ brandId, brand }) => {
    const own = list.filter((rule) => sameBrand(rule.brand_id, brandId));
    let state = 'own';
    if (!own.length) state = brandId && globalActive ? 'covered' : 'none';
    return { brandId, brand, isGlobal: !brandId, rules: own, state };
  });
};

// A brand's INACTIVE rule still wins the lookup for its scopes, so the
// active « Toutes les marques » rule does not apply there: the client or the
// account decides alone. The trap is invisible in a flat list.
export const shadowingRules = (rules) => {
  const list = Array.isArray(rules) ? rules : [];
  const globalActive = list.filter((rule) => !rule.brand_id && rule.is_active);
  return list.filter((rule) => {
    if (!rule.brand_id || rule.is_active) return false;
    const overlap = scopesOf(rule.scope).filter((scope) => globalActive.some((g) => scopesOf(g.scope).includes(scope)));
    if (!overlap.length) return false;
    // An active rule of the same brand on that scope would be found as well.
    const brandActive = list.filter((r) => sameBrand(r.brand_id, rule.brand_id) && r.is_active);
    return overlap.some((scope) => !brandActive.some((r) => scopesOf(r.scope).includes(scope)));
  });
};

// « À vérifier » of the page: only configuration a person has to fix.
export const automationAttention = (rules) => shadowingRules(rules).map((rule) => ({
  key: `shadow-${rule.id}`, kind: 'shadow', tone: 'warning', rule,
}));

// The state tiles. null while unknown (no fake 0).
export const automationKpis = (stats) => {
  if (!stats || typeof stats.total_evaluations !== 'number') return null;
  const total = stats.total_evaluations;
  return {
    total,
    approved: stats.approved_count || 0,
    rejected: stats.rejected_count || 0,
    approvalRate: total > 0 ? (typeof stats.approval_rate === 'number' ? stats.approval_rate : (stats.approved_count || 0) / total) : null,
    avgConfidence: total > 0 && typeof stats.avg_confidence === 'number' ? stats.avg_confidence : null,
    avgSeconds: total > 0 && typeof stats.avg_processing_time_ms === 'number' && stats.avg_processing_time_ms > 0
      ? stats.avg_processing_time_ms / 1000
      : null,
  };
};

// « Qui peut envoyer seul » — each layer only when its GET answered.
// clients: GET /api/logistics/clients-edn; wakeup: GET
// /api/automation/ticket-workflow/wakeup.
export const sendingOverview = ({ clients, wakeup }) => {
  const out = {};
  if (Array.isArray(clients)) {
    out.clients = { total: clients.length, automated: clients.filter((client) => client.automation === true).length };
  }
  if (wakeup && wakeup.wakeup) {
    out.wakeup = { mode: wakeup.wakeup.mode || 'off', liveSince: wakeup.wakeup.live_since || null };
    if (wakeup.assigned && wakeup.assigned.effective) out.assigned = wakeup.assigned.effective;
  }
  return out;
};

// What a rule switch / save / delete changes, for its confirmation.
// 'activate': its replies get judged and may leave alone; 'deactivate' /
// 'delete': the B2B client's setting decides without it.
export const ruleChangeKind = (before, afterActive) => {
  if (!before) return afterActive ? 'activate' : null;
  if (!before.is_active && afterActive) return 'activate';
  if (before.is_active && !afterActive) return 'deactivate';
  return null;
};

// Without the rule, who sends alone without the model's judgment: the
// automated B2B clients.
export const unjudgedAfterRemoval = (overview) => ({
  automatedClients: overview?.clients?.automated || 0,
});

// ---- request shapes (unchanged from the previous page) --------------------

export const emptyRuleForm = (brandId = '') => ({
  brand_id: brandId,
  is_active: true,
  scope: 'all',
  instructions: '',
  confidence_threshold: 0.8,
  fallback_action: 'draft',
  fallback_employee_id: '',
});

export const ruleFormFrom = (rule) => ({
  brand_id: rule.brand_id || '',
  is_active: rule.is_active,
  scope: rule.scope,
  instructions: rule.instructions,
  confidence_threshold: rule.confidence_threshold,
  fallback_action: rule.fallback_action,
  fallback_employee_id: rule.fallback_employee_id || '',
});

// PUT /api/ai-automation/rules/{id} body; POST adds brand_id.
export const rulePayload = (form) => ({
  is_active: form.is_active,
  scope: form.scope,
  instructions: form.instructions,
  confidence_threshold: form.confidence_threshold,
  fallback_action: form.fallback_action,
  fallback_employee_id: form.fallback_employee_id || null,
});

export const ruleCreatePayload = (form) => ({ ...rulePayload(form), brand_id: form.brand_id || null });

// Query of /logs, /logs/stats and /logs/export (same keys, same order as before).
export const logQuery = ({ days, brand, filters, page, limit, withDecision }) => {
  const params = new URLSearchParams(page ? { page, limit, days } : { days });
  if (brand) params.append('brand_id', brand);
  if (filters.scope) params.append('scope', filters.scope);
  if (filters.source) params.append('source', filters.source);
  if (filters.topic) params.append('topic', filters.topic);
  if (filters.assistant) params.append('assistant_id', filters.assistant);
  if (filters.q) params.append('q', filters.q);
  if (withDecision && filters.decision) params.append('decision', filters.decision);
  return params.toString();
};

export const journalFiltersActive = (brand, filters) => Boolean(
  brand || filters.scope || filters.source || filters.topic || filters.assistant || filters.q,
);

export const insightsQuery = ({ days, brand, scope }) => {
  const params = new URLSearchParams({ days });
  if (brand) params.append('brand_id', brand);
  if (scope) params.append('scope', scope);
  return params.toString();
};

export const insightsPayload = ({ days, brand, scope, language }) => ({
  days: Number(days),
  brand_id: brand || null,
  scope: scope || null,
  language: (language || 'fr').split('-')[0],
});

// detail « not_enough_rejections:<found>:<min> » of POST /insights/generate.
export const parseNotEnough = (detail) => {
  if (typeof detail !== 'string' || !detail.startsWith('not_enough_rejections:')) return null;
  const [, found, min] = detail.split(':');
  return { found, min };
};
